import { exportDataGrid } from "devextreme/pdf_exporter";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import notify from "devextreme/ui/notify";

export const onExporting = (e) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const dataGrid = e.component;

  // Load the data source using items() to get the actual data
  let data = dataGrid.getDataSource().items();

  console.log("Loaded data:", data);

  if (!Array.isArray(data)) {
    console.error("Data loaded is not an array:", data);
    return;
  }

  // Filter selected rows if selectedRowsOnly is true
  if (e.selectedRowsOnly) {
    const selectedKeys = e.selectedRows || [];
    console.log("Selected Keys for export:", selectedKeys);

    // Ensure you're filtering by the correct identifier (e.g., batchIntakeID)
    data = data.filter((item) => selectedKeys.includes(item.batchIntakeID));

    console.log("Filtered data for export:", data);
  }

  // Now proceed with exporting the filtered data
  exportDataGrid({
    jsPDFDocument: doc,
    component: e.component,
    dataSource: {
      load: () => Promise.resolve(data), // Pass the filtered data directly here
    },
    margin: { top: 10, left: 10, right: 10, bottom: 10 },
    columnWidths: [45, 18, 40, 25, 22, 15, 15, 15, 30],
    customizeCell: ({ gridCell, pdfCell }) => {
      if (
        gridCell &&
        (gridCell.column.dataField == "qcp" ||
          gridCell.column.dataField == "ccp")
      ) {
        pdfCell.text = gridCell.value ? "Done" : ""; // Replace true/false with appropriate text
      }

      if (pdfCell) {
        pdfCell.style = { cellPadding: 2, fontSize: 10 };
      }
    },
  }).then(() => {
    doc.save("Batches.pdf");
  });
};

export const onExportingQCP = (e) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  exportDataGrid({
    jsPDFDocument: doc,
    component: e.component,
    margin: { top: 10, left: 10, right: 10, bottom: 10 },
    customizeCell: ({ gridCell, pdfCell }) => {
      if (pdfCell) {
        pdfCell.style = { cellPadding: 2, fontSize: 10 };
      }
    },
  }).then(() => {
    doc.save("QCPs.pdf");
  });
};

export function findFilletByBatchAndPID(dataArray, singleObject) {
  const { BatchId, pid } = singleObject;

  // Find the batch that matches the BatchId
  const matchingBatch = dataArray.find((batch) => batch.BatchId === BatchId);

  if (!matchingBatch) {
    return null; // Batch not found
  }

  // Find the fillet inside the batch that matches the PID
  const matchingFillet = matchingBatch.FilletDetails.find(
    (fillet) => fillet.PID === pid
  );

  return matchingFillet || null; // Return fillet or null if not found
}

export const onExportingExcel = (e) => {
  let userDetails = JSON.parse(localStorage.getItem("userDetails"));
  if (!e || !e.component) {
    console.error("Invalid event object passed to onExportingExcel.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Data");
  const dataGrid = e.component;

  const name = `${userDetails?.firstName ?? "N/A"} ${
    userDetails?.lastName ?? "N/A"
  }`;
  const date = new Date().toISOString().split("T")[0];

  // 🟢 Add name and date rows above header
  worksheet.addRow(["Name:", capitalizeWords(name)]);
  worksheet.addRow(["Date:", date]);
  worksheet.addRow([]); // Empty row before headers

  // 🟡 Add column headers manually
  const columnHeaders = [
    "Supplier Name",
    "Batch Num",
    "Location",
    "Status",
    "Intake Date",
    "QCP",
    "CCP",
    "Price per KG (€)",
    "Added Value %",
    "Claim Amount (€)",
    "Change %",
    "Size (kg)",
    "Quality",
    "Purchase Ref",
  ];

  const headerRow = worksheet.addRow(columnHeaders);
  headerRow.font = { bold: true };

  // 🟠 Set column widths manually (match the number of headers)
  const columnWidths = [18, 15, 20, 15, 18, 15, 15, 18, 20, 15, 18, 18];
  columnWidths.forEach((width, index) => {
    worksheet.getColumn(index + 1).width = width;
  });

  // 🔁 Fetch and format data
  dataGrid
    .getDataSource()
    .store()
    .load()
    .then((result) => {
      let data = result?.data || result;

      if (!Array.isArray(data)) {
        console.error("Data loaded is not an array:", result);
        return;
      }

      if (e.selectedRowsOnly) {
        data = data?.filter((item) =>
          e?.selectedRows?.includes(item.batchIntakeID)
        );
      }

      data.forEach((item) => {
        const formattedItem = [
          item?.supplier?.name ?? "N/A",
          item?.batchNumber ?? "N/A",
          item?.location?.addressLine ?? "N/A",
          item?.status?.description ?? "N/A",
          item?.intakeDate
            ? new Date(item.intakeDate).toISOString().split("T")[0]
            : "N/A",
          item?.qcp !== undefined ? (item.qcp ? "Done" : "") : "N/A",
          item?.ccp !== undefined ? (item.ccp ? "Done" : "") : "N/A",
          item?.pricePerKG ?? "N/A",
          item.claimAmount !== 0
            ? (
                (item.claimAmount / (item.pricePerKG * item.volume)) *
                100
              ).toFixed(2)
            : "0",
          item?.claimAmount ?? "N/A",
          item.claimAmount !== 0
            ? (item.pricePerKG / item.claimAmount).toFixed(2)
            : "0",

          item?.productSize?.description ??
            item?.productSize?.productSizeCode ??
            "N/A",
          item?.productQuality?.description ??
            item?.productQuality?.productQualityCode ??
            "N/A",

          item?.purchaseRef ?? "N/A",
        ];

        const row = worksheet.addRow(formattedItem);
        row.eachCell((cell) => {
          cell.alignment = { horizontal: "left" };
        });
      });

      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "Data.xlsx"
        );
      });
    });
};

export const onExportingExcelQCP = (e) => {
  let userDetails = JSON.parse(localStorage.getItem("userDetails"));
  if (!e || !e.component) {
    console.error("Invalid event object passed to onExportingExcel.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Data");
  const dataGrid = e.component;

  // 🟢 Add name and date rows first
  const name = `${userDetails?.firstName ?? "N/A"} ${
    userDetails?.lastName ?? "N/A"
  }`;
  const date = new Date().toISOString().split("T")[0];

  worksheet.addRow(["Name:", capitalizeWords(name)]);
  worksheet.addRow(["Date:", date]);
  worksheet.addRow([]); // Empty row for spacing

  // 🟡 Add column headers manually as a row
  const columnHeaders = [
    "Batch Num",
    "Box Num",
    // "Inspected By",
    // "Approved By",
    // "Quality",
    // "Temp",
    // "Date",
  ];
  const headerRow = worksheet.addRow(columnHeaders);
  headerRow.font = { bold: true };

  // 🟠 Set column widths manually
  const columnWidths = [15, 15, 15, 15, 15, 15, 15];
  columnWidths.forEach((width, index) => {
    worksheet.getColumn(index + 1).width = width;
  });

  // ✅ Load the data source
  dataGrid
    .getDataSource()
    .store()
    .load()
    .then((result) => {
      let data = result?.data || result;
      console.log("result", result);
      if (!Array.isArray(data)) {
        console.error("Data loaded is not an array:", result);
        return;
      }

      console.log("Selected row IDs:", e.selectedRows);
      console.log("Available data IDs:", data);

      // ✅ Filter selected rows if selectedRowsOnly is true
      if (e.selectedRowsOnly) {
        data = data?.filter((item) => e?.selectedRows?.includes(item.qcP_ID)); // Adjust key as needed
        console.log("Filtered data for export:", data);
      }

      data.forEach((item) => {
        const flattenedItem = {
          batchNo: item?.batchNo ?? "N/A",
          boxNo: item?.boxNo ?? "N/A",
          // inspectedBy: item?.inspectedBy ?? "N/A",
          // approvedBy: item?.approvedBy ?? "N/A",
          // boxQuality: item?.boxData?.quality ?? "N/A",
          // boxTemp: item?.boxData?.temp ?? "N/A",
          // createdDateUTC: item?.createdDateUTC
          //   ? new Date(item.createdDateUTC).toISOString().split('T')[0]
          //   : "N/A",
        };

        const row = worksheet.addRow(Object.values(flattenedItem));

        // Explicitly set alignment for all cells in the row to left
        row.eachCell((cell) => {
          cell.alignment = { horizontal: "left" };
        });
      });

      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "QCP_Data.xlsx"
        );
      });
    });
};

export const onExportingCCPExcel = (e) => {
  if (!e || !e.component) {
    console.error("Invalid event object passed to onExportingExcel.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Data");
  const dataGrid = e.component;

  // 🟢 Add name and date rows first
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const name = `${userDetails?.firstName ?? "N/A"} ${
    userDetails?.lastName ?? "N/A"
  }`;
  const date = new Date().toISOString().split("T")[0];

  worksheet.addRow(["Name:", capitalizeWords(name)]);
  worksheet.addRow(["Date:", date]);
  worksheet.addRow([]); // Add an empty row for spacing

  // 🟡 Add column headers manually as a row
  const columnHeaders = [
    "Batch No",
    "License Plate",
    "Company",
    "Driver",
    "Truck Temp",
    "Truck Clean",
  ];
  const headerRow = worksheet.addRow(columnHeaders);
  headerRow.font = { bold: true };

  // 🟠 Set column widths manually
  const columnWidths = [15, 15, 15, 15, 15, 15];
  columnWidths.forEach((width, index) => {
    worksheet.getColumn(index + 1).width = width;
  });

  // ✅ Load the data source
  dataGrid
    .getDataSource()
    .store()
    .load()
    .then((result) => {
      let data = result?.data || result;

      if (!Array.isArray(data)) {
        console.error("Data loaded is not an array:", result);
        return;
      }

      // ✅ Filter selected rows if selectedRowsOnly is true
      if (e.selectedRowsOnly) {
        data = data?.filter((item) => e?.selectedRows?.includes(item.ccP_ID));
      }

      data.forEach((item) => {
        const flattenedItem = [
          item?.batchNo ?? "N/A",
          item?.licensePlate ?? "N/A",
          item?.company ?? "N/A",
          item?.driver ?? "N/A",
          item?.truckTemp ?? "N/A",
          item?.truckClean ?? "N/A",
        ];

        const row = worksheet.addRow(flattenedItem);

        // Explicitly set alignment for all cells in the row to left
        row.eachCell((cell) => {
          cell.alignment = { horizontal: "left" };
        });
      });

      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "CCP.xlsx"
        );
      });
    });
};

export const urlToFile = async (url, filename) => {
  const res = await fetch(url);
  const contentType = res.headers.get("content-type") || "image/png"; // fallback if missing
  const blob = await res.blob();
  return new File([blob], filename, { type: contentType });
};

export const capitalizeWords = (text) => {
  return text
    ? text
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
    : "";
};

export function showNotification(message, type = "info") {
  notify(
    {
      message: message,
      type: type,
      width: 300,
      closeOnClick: true,
      displayTime: 5000,
      animation: {
        show: {
          type: "slide",
          from: { top: -50, opacity: 0 },
          to: { top: 0, opacity: 1 },
          duration: 400,
        },
        hide: {
          type: "slide",
          from: { top: 0, opacity: 1 },
          to: { top: -50, opacity: 0 },
          duration: 400,
        },
      },
      position: {
        my: "top right",
        at: "top right",
        offset: "20 20", // 20px from top and right edges
        of: window, // Key change: relative to viewport
        collision: "fit flip",
      },
    },
    {
      direction: "down-push",
      target: "body", // Changed to body element
      // Add these CSS properties to ensure proper positioning
      cssClass: "fixed-notification",
    }
  );
}

export function toLocalISOString(date) {
  const pad = (n) => String(n).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  // 👇 Append Z to mimic ISO format, but it's actually local time
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}Z`;
}
